import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, ChevronRight, AlertCircle, CheckCircle, Clock, Loader, History, X } from 'lucide-react';
import api from '../services/api';
import { format } from 'date-fns';
import WorkflowHistoryDetailModal from '../components/WorkflowHistoryDetailModal';

interface WorkflowHistoryItem {
  id: string;
  customer_id: string;
  admin_account: string;
  status: 'INITIATED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  current_step: string;
  total_accounts: number;
  processed_accounts: number;
  spreadsheet_url?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

const WorkflowHistory: React.FC = () => {
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['workflow-history', searchTerm, statusFilter, currentPage],
    queryFn: () => api.getWorkflowHistory({
      customer_id: searchTerm || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      limit,
      offset: (currentPage - 1) * limit,
    }),
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  const workflows = historyData?.workflows || [];
  const total = historyData?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'FAILED':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'PROCESSING':
        return <Loader className="w-5 h-5 text-orange-400 animate-spin" />;
      case 'CANCELLED':
        return <X className="w-5 h-5 text-gray-400" />;
      default:
        return <Clock className="w-5 h-5 text-blue-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'FAILED':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'PROCESSING':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'CANCELLED':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  const getDuration = (workflow: WorkflowHistoryItem) => {
    const start = new Date(workflow.created_at);
    const end = workflow.completed_at ? new Date(workflow.completed_at) : new Date();
    const durationMs = end.getTime() - start.getTime();

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <History className="w-8 h-8 text-orange-500" />
            <h1 className="text-3xl font-bold premium-gradient">Workflow History</h1>
          </div>
          <p className="text-gray-400 mt-1">View and audit past workflow executions</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Total Workflows</div>
          <div className="text-2xl font-bold text-white">{total}</div>
        </div>
        <div className="stat-card stat-card-green">
          <div className="text-xs font-semibold uppercase tracking-wider text-green-500 mb-2">Completed</div>
          <div className="text-2xl font-bold text-white">
            {workflows.filter((w: WorkflowHistoryItem) => w.status === 'COMPLETED').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-red-500 mb-2">Failed</div>
          <div className="text-2xl font-bold text-white">
            {workflows.filter((w: WorkflowHistoryItem) => w.status === 'FAILED').length}
          </div>
        </div>
        <div className="stat-card stat-card-orange">
          <div className="text-xs font-semibold uppercase tracking-wider text-orange-500 mb-2">In Progress</div>
          <div className="text-2xl font-bold text-white">
            {workflows.filter((w: WorkflowHistoryItem) => w.status === 'PROCESSING' || w.status === 'INITIATED').length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by Customer ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-500 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1); // Reset to first page on filter change
              }}
              className="px-4 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500/50"
            >
              <option value="all">All Status</option>
              <option value="INITIATED">Initiated</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="dashboard-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Workflow ID</th>
                <th className="px-6 py-3 text-left">Customer</th>
                <th className="px-6 py-3 text-left">Last Step</th>
                <th className="px-6 py-3 text-left">Accounts</th>
                <th className="px-6 py-3 text-left">Duration</th>
                <th className="px-6 py-3 text-left">Created</th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <Loader className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
                    <p className="text-gray-400 mt-2">Loading history...</p>
                  </td>
                </tr>
              ) : workflows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-400">
                    No workflow history found
                  </td>
                </tr>
              ) : (
                workflows.map((workflow: WorkflowHistoryItem) => (
                  <tr key={workflow.id} className="table-row">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(workflow.status)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(workflow.status)}`}>
                          {workflow.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-mono text-sm text-gray-300">{workflow.id.substring(0, 8)}...</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white font-medium">{workflow.customer_id}</div>
                      {workflow.admin_account && (
                        <div className="text-xs text-gray-500">{workflow.admin_account}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{workflow.current_step || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">
                        {workflow.processed_accounts}/{workflow.total_accounts}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{getDuration(workflow)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-400">
                        {format(new Date(workflow.created_at), 'MMM dd, yyyy HH:mm')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedWorkflowId(workflow.id)}
                        className="p-1.5 text-gray-400 hover:text-orange-400 transition-colors"
                        title="View Details"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700/50">
            <div className="text-sm text-gray-400">
              Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of {total} workflows
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedWorkflowId && (
        <WorkflowHistoryDetailModal
          workflowId={selectedWorkflowId}
          onClose={() => setSelectedWorkflowId(null)}
        />
      )}
    </div>
  );
};

export default WorkflowHistory;
